import { useEffect, useMemo, useState } from "react"
import { CurrencyConvertion, PriceCheck, QatarConvertion, FormatValueDecimal } from "../../utils/Validate";
import AmountModeField from "../../components/common/AmountModeField";
import SpecialColorDetailsModal from "../../components/common/SpecialColorDetailsModal";

const getProductMatchKeys = (item) => [
    item?.customerOrderProductId,
    item?.quoteProductId,
    item?.orderProductId,
    item?.id,
].filter((value) => value !== null && value !== undefined && value !== "");

const getProductItemMatchKeys = (item) => [
    item?.customerOrderProductItemId,
    item?.quoteProductItemId,
    item?.orderProductItemId,
    item?.customerOrderItemId,
    item?.id,
].filter((value) => value !== null && value !== undefined && value !== "");

const getProductItems = (item) =>
    Array.isArray(item?.productItems) ? item.productItems : [];

const findFactoryProductItem = (sourceItem, factoryProduct) => {
    const itemKeys = getProductItemMatchKeys(sourceItem);

    return factoryProduct?.productItems?.find((factoryItem) =>
        getProductItemMatchKeys(factoryItem).some((key) =>
            itemKeys.some((itemKey) => String(itemKey) === String(key))
        )
    );
};


const getLineListPrice = (item, factoryOrderRecord) => {
    if (item?.isInCurrentOrder === false) {
        return 0;
    }
    const factoryProduct = factoryOrderRecord?.products?.find((product) =>
        getProductMatchKeys(product).some((key) =>
            getProductMatchKeys(item).some((itemKey) => String(itemKey) === String(key))
        )
    );

    if (Number(item?.factoryReleasedQuantity ?? 0) === 0) {
        return 0;
    }
    if (Number(factoryProduct?.currentReleaseQuantity ?? 0) === 0) {
        return 0;
    }

    const productItems = getProductItems(item);

    if (productItems.length > 0) {
        return productItems.reduce((total, productItem) => {
            const matchingProductItem = findFactoryProductItem(productItem, factoryProduct);
            const releaseQty = Number(
                matchingProductItem?.currentReleaseQuantity ??
                matchingProductItem?.factoryReleasedQuantity ??
                productItem?.currentReleasedQuantity ??
                productItem?.factoryReleasedQuantity ??
                0
            );

            if (Number(matchingProductItem?.id ?? productItem?.id ?? 0) === 0) {
                return total;
            }

            if (releaseQty === 0) {
                return total;
            }

            const listPrice = Number(
                matchingProductItem?.listPrice ??
                productItem?.listPrice ??
                0
            );

            return total + (listPrice * releaseQty);
        }, 0);
    }

    const matchingProductItem = findFactoryProductItem(item, factoryProduct);

    if (Number(matchingProductItem?.id ?? item?.id ?? 0) === 0) {
        return 0;
    }

    const currentReleaseQty = Number(
        matchingProductItem?.currentReleaseQuantity ??
        matchingProductItem?.factoryReleasedQuantity ??
        factoryProduct?.currentReleaseQuantity ??
        0
    );

    return (
        Number(matchingProductItem?.listPrice ?? item?.listPrice ?? item?.unitPrice ?? 0) *
        currentReleaseQty
    );
};


const getLineNetPrice = (item, factoryOrderRecord) => {
    if (item?.isInCurrentOrder === false) {
        return 0;
    }
    const factoryProduct = factoryOrderRecord?.products?.find((product) =>
        getProductMatchKeys(product).some((key) =>
            getProductMatchKeys(item).some((itemKey) => String(itemKey) === String(key))
        )
    );
    
    if (Number(factoryProduct?.currentReleaseQuantity ?? 0) === 0) {
        return 0;
    }

    const productItems = getProductItems(item);

    if (productItems.length > 0) {
        return productItems.reduce((total, productItem) => {
            const matchingProductItem = findFactoryProductItem(productItem, factoryProduct);
            const releaseQty = Number(
                matchingProductItem?.currentReleaseQuantity ??
                matchingProductItem?.factoryReleasedQuantity ??
                productItem?.currentReleasedQuantity ??
                productItem?.factoryReleasedQuantity ??
                0
            );

            if (Number(matchingProductItem?.id ?? productItem?.id ?? 0) === 0) {
                return total;
            }

            if (releaseQty === 0) {
                return total;
            }

            const unitPrice = Number(
                matchingProductItem?.unitPrice ??
                productItem?.unitPrice ??
                0
            );
            const lineAmount = unitPrice * releaseQty;

            return total + lineAmount;
        }, 0);
    }

    const matchingProductItem = findFactoryProductItem(item, factoryProduct);

    if (Number(matchingProductItem?.id ?? item?.id ?? 0) === 0) {
        return 0;
    }

    const currentReleaseQty = Number(
        matchingProductItem?.currentReleaseQuantity ??
        matchingProductItem?.factoryReleasedQuantity ??
        factoryProduct?.currentReleaseQuantity ??
        0
    );

            const unitPrice = Number(
                matchingProductItem?.unitPrice ??
                0
            );
           
            const lineAmount = unitPrice * currentReleaseQty;

            return lineAmount;
};
const FactoryOrderProductSummary = ({ values, setValues, currencyDetail, defaultCurrencyDetail, setQuotUserEntry, quotUserEntry, factoryOrderRecord }) => {
    // Factory line prices and calculated totals are stored in the base
    // currency.  The commercial summary is shown in the customer's default
    // currency, so apply that currency's conversion rate here.
    const displayCurrency = defaultCurrencyDetail || currencyDetail;
    const displayAmount = (amount) => CurrencyConvertion(
        amount,
        displayCurrency?.conversionRate,
        displayCurrency
    );
    const [taxMode, setTaxMode] = useState('$');
    const [customDutyMode, setCustomDutyMode] = useState('$');
    const [clearnceMode, setClearnceMode] = useState('$');
    const [specialColorModalShow, setSpecialColorModalShow] = useState(false);

  const productsSignature = JSON.stringify(
    (values?.products || []).map((item) => ({
        id: item?.id,
        isInCurrentOrder: item?.isInCurrentOrder,
        currentReleasedQuantity: item?.currentReleasedQuantity,
        releasedQuantity: item?.releasedQuantity,
        factoryReleasedQuantity: item?.factoryReleasedQuantity,
        lpoQuantity: item?.lpoQuantity,
        qtyValues: item?.qtyValues,
        quoteQuantity: item?.quoteQuantity,
        listPrice: item?.listPrice,
        unitPrice: item?.unitPrice,
        unitNetPrice: item?.unitNetPrice,
        sellingPrice: item?.sellingPrice,
        netPrice: item?.netPrice,

        productItems: Array.isArray(item?.productItems)
            ? item.productItems.map((productItem) => ({
                id: productItem?.id,
                isInCurrentOrder: productItem?.isInCurrentOrder,
                currentReleasedQuantity: productItem?.currentReleasedQuantity,
                releasedQuantity: productItem?.releasedQuantity,
                factoryReleasedQuantity: productItem?.factoryReleasedQuantity,
                lpoQuantity: productItem?.lpoQuantity,
                qtyValues: productItem?.qtyValues,
                quoteQuantity: productItem?.quoteQuantity,
                listPrice: productItem?.listPrice,
                unitPrice: productItem?.unitPrice,
                unitNetPrice: productItem?.unitNetPrice,
                sellingPrice: productItem?.sellingPrice,
                netPrice: productItem?.netPrice,
            }))
            : [],
    }))
);

const quotationSummary = useMemo(() => {
    if (!values?.products?.length) {
        return {
            totalListPrice: 0,
            netPrice: 0,
        };
    }

    let totalListPrice = 0;
    let netPrice = 0;

    values.products.forEach((item) => {
        totalListPrice += getLineListPrice(item, factoryOrderRecord);
        netPrice += getLineNetPrice(item, factoryOrderRecord);
    });

    return {
        totalListPrice,
        netPrice,
    };
}, [productsSignature, factoryOrderRecord]);

    const HandleCustomDutyModeChange = () => {
        setCustomDutyMode((prevMode) => (prevMode === '$' ? '%' : '$'));
        setQuotUserEntry({ ...quotUserEntry, customDutyPercentage: 0, customDuty: 0 })
    };

    const HandleClearnceModeChange = () => {
        setClearnceMode((prevMode) => (prevMode === '$' ? '%' : '$'));
        setQuotUserEntry({ ...quotUserEntry, clearanceChargesPercentage: 0, clearanceCharges: 0 })
    };

    const HandleTaxModeChange = () => {
        setTaxMode((prevMode) => (prevMode === '$' ? '%' : '$'));
        setQuotUserEntry({ ...quotUserEntry, tax: 0, taxPercentage: 0 })
    };

    // useEffect(() => {
    //     setValues((prev) => ({
    //         ...prev,
    //         totalListPrice: Number(quotationSummary?.totalListPrice || 0),
    //         netPrice: Number(quotationSummary?.netPrice || 0),
    //     }));
    // }, [quotationSummary, setValues]);


    const calculateTotalDdp = (item, freightCustoms) => {
        const sellingPrice = getLineNetPrice(item, factoryOrderRecord) || 0;

        if (freightCustoms >= 1) return 0;

        const ddpMultiplier = 1 - freightCustoms;
        const finalDdp = sellingPrice / (ddpMultiplier || 1);

        if (!Number.isFinite(finalDdp)) return 0;

        return parseFloat(finalDdp.toFixed(2));
    };
const hasFactoryRelease = (values?.products || []).some((item) => {
    if (item?.isInCurrentOrder === false) {
        return false;
    }

    const itemReleasedQty = Number(item?.factoryReleasedQuantity ?? 0);

    const productItems = Array.isArray(item?.productItems)
        ? item.productItems
        : [];

    if (productItems.length > 0) {
        return productItems.some(
            (productItem) =>
                productItem?.isInCurrentOrder !== false &&
                Number(productItem?.factoryReleasedQuantity ?? 0) > 0
        );
    }

    return itemReleasedQty > 0;
});
    useEffect(() => {
    const calculatedNetPrice = Number(
        quotationSummary?.netPrice || 0
    );

    const calculatedListPrice = Number(
        quotationSummary?.totalListPrice || 0
    );

     if (!hasFactoryRelease) {
        setValues((prev) => ({
            ...prev,
            totalListPrice: 0,
            netPrice: 0,
            totalExworksPrice: 0,
            totalLandedCost: 0,
            totalSellingPrice: 0,
            grandTotal: 0,
            products: (prev?.products || []).map((item) => ({
                ...item,
                totalDdp: 0,
            })),
        }));

        return;
    }

    const totalExworksPrice =
        calculatedNetPrice + Number(values?.specialCharges || 0);
        const customDutyValue = customDutyMode === '$' ? (values?.customDuty * 1) : (((totalExworksPrice * 1) + (values?.freightCharges * 1)) * ((values?.customDutyPercentage * 1) / 100))
        const clearnceValue = clearnceMode === '$' ? (values?.clearanceCharges * 1) : (((totalExworksPrice * 1) + (values?.freightCharges * 1) + (customDutyValue * 1)) * ((values?.clearanceChargesPercentage * 1) / 100))
        const totalLandedCost = ((totalExworksPrice * 1) + (values?.freightCharges * 1) + (customDutyValue * 1) + (clearnceValue * 1))
        const marginFraction = Math.min((values?.salesMarginPercentage * 1 || 0) / 100, 0.999999)
        const salesMarginValue = (totalLandedCost * 1) / (1 - marginFraction)
        const taxValue = taxMode === '$' ? (values?.tax * 1) : ((salesMarginValue * 1) * ((values?.taxPercentage * 1) / 100))
        const grandTotal = salesMarginValue + taxValue
      const frieghtcustoms = salesMarginValue > 0
    ? (salesMarginValue - calculatedNetPrice) / salesMarginValue
    : 0;

        const productsArr = values?.products?.map((item) => {
            const totalDdp = calculateTotalDdp(item, frieghtcustoms);
            return { ...item, totalDdp };
        });

       setValues((prev) => ({
    ...prev,
    totalListPrice: calculatedListPrice,
    netPrice: calculatedNetPrice,
    products: productsArr,
    totalSellingPrice: Number(salesMarginValue) || 0,
    totalExworksPrice: Number(totalExworksPrice) || 0,
    grandTotal: Number(grandTotal) || 0,
    totalLandedCost: Number(totalLandedCost) || 0,
}));
    }, [
        values?.specialCharges,
        values?.netPrice,
        values?.customDuty,
        values?.customDutyPercentage,
        values?.freightCharges,
        values?.clearanceCharges,
        values?.clearanceChargesPercentage,
        values?.salesMarginPercentage,
        values?.tax,
        values?.taxPercentage,
        values?.products?.length,
        clearnceMode, customDutyMode, taxMode
    ]);

    useEffect(() => {
        if (values?.customDuty > 0) setCustomDutyMode('$')
        else setCustomDutyMode('%')
        if (values?.tax > 0) setTaxMode('$')
        else setTaxMode('%')
        if (values?.clearanceCharges > 0) setClearnceMode('$')
        else setClearnceMode('%')
        setQuotUserEntry({
            specialCharges: values?.specialCharges || 0,
            freightCharges: values?.freightCharges || 0,
            customDuty: values?.customDuty || 0,
            customDutyPercentage: values?.customDutyPercentage || 0,
            clearanceCharges: values?.clearanceCharges || 0,
            clearanceChargesPercentage: values?.clearanceChargesPercentage || 0,
            salesMarginPercentage: values?.salesMarginPercentage || 0,
            tax: values?.tax || 0,
            taxPercentage: values?.taxPercentage || 0,
        });
        
    }, [values.id])

    const HandleInputChange = (e) => {
        const { name, value } = e.target;
        setQuotUserEntry({ ...quotUserEntry, [name]: value || 0 })
    }

useEffect(() => {
    const netPrice = Number(quotationSummary?.netPrice || 0);
    const totalListPrice = Number(quotationSummary?.totalListPrice || 0);

    const specialCharges = Number(values?.specialCharges || 0);
    const freightCharges = Number(values?.freightCharges || 0);

    const customDuty = Number(values?.customDuty || 0);
    const customDutyPercentage = Number(values?.customDutyPercentage || 0);

    const clearanceCharges = Number(values?.clearanceCharges || 0);
    const clearanceChargesPercentage =
        Number(values?.clearanceChargesPercentage || 0);

    const salesMarginPercentage =
        Number(values?.salesMarginPercentage || 0);

    const tax = Number(values?.tax || 0);
    const taxPercentage = Number(values?.taxPercentage || 0);

    // 1. Total Ex-Works
    const totalExworksPrice =
        netPrice + specialCharges;

    // 2. Custom Duty
    const customDutyValue =
        customDutyMode === "$"
            ? customDuty
            : ((totalExworksPrice + freightCharges) *
                customDutyPercentage) / 100;

    // 3. Clearance
    const clearanceValue =
        clearnceMode === "$"
            ? clearanceCharges
            : ((totalExworksPrice +
                freightCharges +
                customDutyValue) *
                clearanceChargesPercentage) / 100;

    // 4. Total Landed Cost
    const totalLandedCost =
        totalExworksPrice +
        freightCharges +
        customDutyValue +
        clearanceValue;

    // 5. Total Selling Price
    const marginFraction = Math.min(
        salesMarginPercentage / 100,
        0.999999
    );

    const totalSellingPrice =
        totalLandedCost / (1 - marginFraction);
    // 6. Tax
    const taxValue =
        taxMode === "$"
            ? tax
            : (totalSellingPrice * taxPercentage) / 100;

    // 7. Grand Total
    const grandTotal =
        totalSellingPrice + taxValue;
    // 8. DDP
    const freightCustoms =
        totalSellingPrice > 0
            ? (totalSellingPrice - netPrice) / totalSellingPrice
            : 0;

    const productsArr = (values?.products || []).map(item => ({
        ...item,
        totalDdp: calculateTotalDdp(item, freightCustoms),
    }));

    setValues(prev => ({
        ...prev,
        totalListPrice,
        netPrice,
        totalExworksPrice,
        totalLandedCost,
        totalSellingPrice,
        grandTotal,
        products: productsArr,
    }));

}, [
    quotationSummary?.netPrice,
    quotationSummary?.totalListPrice,
    values?.specialCharges,
    values?.freightCharges,
    values?.customDuty,
    values?.customDutyPercentage,
    values?.clearanceCharges,
    values?.clearanceChargesPercentage,
    values?.salesMarginPercentage,
    values?.tax,
    values?.taxPercentage,
    customDutyMode,
    clearnceMode,
    taxMode,
]);

    return (
        <div className="col-xxl-2 col-xl-2 col-lg-3 col-md-12 col-sm-12 col-xs-12 right-side-card order-summary-side-card">
            <div className="card order-commercial-summary">
                <div className="card-header">
                    <h5>{displayCurrency?.shortCode} : {FormatValueDecimal(displayAmount(values?.grandTotal))}</h5>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
                            <div className="accordion" id="orderSummaryAccordion">
                                <div className="row">
                                    <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
                                        <div className="mb-2">
                                            <label>Net price</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                name="netPrice"
                                                placeholder="Enter"
                                                value={FormatValueDecimal(displayAmount(values?.netPrice))}
                                                disabled
                                                style={{ textAlign: "right" }}
                                            ></input>
                                        </div>

                                        <div className="mb-2 ">
                                            <div className="d-flex align-items-center">
                                                <label className="mr-1">Special color</label>
                                                <button
                                                    type="button"
                                                    className="btn p-0 mb-1"
                                                    aria-label="Open special color details"
                                                    onClick={() => setSpecialColorModalShow(true)}
                                                >
                                                    <i className="fas fa-angle-right"></i>
                                                </button>
                                            </div>
                                            <input
                                                autoComplete="off"
                                                type="number"
                                                name="specialCharges"
                                                step=".01"
                                                min="0"
                                                className="form-control"
                                                placeholder="Enter"
                                                onChange={HandleInputChange}
                                                value={quotUserEntry.specialCharges > 0 ? quotUserEntry.specialCharges : ""}
                                                onKeyPress={(e) => { return PriceCheck(e.target.value) }}
                                                style={{ textAlign: "right" }}
                                                onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                                            />
                                        </div>

                                        <div className="mb-2">
                                            <label>Total Ex-Works price</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                name="totalExworksPrice"
                                                placeholder="Enter"
                                                value={FormatValueDecimal(displayAmount(values?.totalExworksPrice))}
                                                disabled
                                                style={{ textAlign: "right" }}
                                            ></input>
                                        </div>

                                        <div className="mb-2">
                                            <label>Frieght & delivery charges</label>
                                            <input
                                                autoComplete="off"
                                                type="number"
                                                name="freightCharges"
                                                step=".01"
                                                min="0"
                                                className="form-control"
                                                placeholder="Enter"
                                                onChange={HandleInputChange}
                                                value={quotUserEntry?.freightCharges > 0 ? quotUserEntry?.freightCharges : ""}
                                                onKeyPress={(e) => { return PriceCheck(e.target.value) }}
                                                style={{ textAlign: "right" }}
                                                onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                                            />
                                        </div>

                                        <div className="mb-2">
                                            <label>Custom Duty</label>
                                            <AmountModeField
                                                name={customDutyMode === '$' ? 'customDuty' : 'customDutyPercentage'}
                                                step=".01"
                                                min="0"
                                                onChange={HandleInputChange}
                                                onModeToggle={HandleCustomDutyModeChange}
                                                mode={customDutyMode}
                                                value={customDutyMode === '$' ? quotUserEntry?.customDuty > 0 ? quotUserEntry?.customDuty : "" :
                                                    quotUserEntry?.customDutyPercentage > 0 ? quotUserEntry?.customDutyPercentage : ""}
                                                onKeyPress={(e) => { return PriceCheck(e.target.value) }}
                                                onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <label>Clearance charges</label>
                                            <AmountModeField
                                                name={clearnceMode === '$' ? 'clearanceCharges' : 'clearanceChargesPercentage'}
                                                step=".01"
                                                min="0"
                                                onChange={HandleInputChange}
                                                onModeToggle={HandleClearnceModeChange}
                                                mode={clearnceMode}
                                                value={clearnceMode === '$' ? quotUserEntry?.clearanceCharges > 0 ? quotUserEntry?.clearanceCharges : "" :
                                                    quotUserEntry?.clearanceChargesPercentage > 0 ? quotUserEntry?.clearanceChargesPercentage : ""}
                                                onKeyPress={(e) => { return PriceCheck(e.target.value) }}
                                                onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                                            />
                                        </div>

                                        <div className="mb-2">
                                            <label>Total Landed Cost</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                placeholder="Enter"
                                                name="totalLandedCost"
                                                disabled
                                                value={values?.totalLandedCost > 0 ? FormatValueDecimal(displayAmount(values?.totalLandedCost)) : 0}
                                                style={{ textAlign: "right" }}
                                            ></input>
                                        </div>

                                        <div className="mb-2">
                                            <label>Sales Margin (%)</label>
                                            <input
                                                autoComplete="off"
                                                type="number"
                                                name="salesMarginPercentage"
                                                step=".01"
                                                min="0"
                                                className="form-control"
                                                placeholder="Enter"
                                                onChange={HandleInputChange}
                                                value={quotUserEntry?.salesMarginPercentage > 0 ? quotUserEntry?.salesMarginPercentage : ""}
                                                onKeyPress={(e) => { return PriceCheck(e.target.value) }}
                                                style={{ textAlign: "right" }}
                                                onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                                            />
                                        </div>

                                        <div className="mb-2">
                                            <label>Total Selling Price</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                name="totalSellingPrice"
                                                placeholder="Enter"
                                                value={FormatValueDecimal(displayAmount(values?.totalSellingPrice))}
                                                disabled
                                                style={{ textAlign: "right" }}
                                            ></input>
                                        </div>
                                        <div className="mb-2">
                                            <label>Tax</label>
                                            <AmountModeField
                                                name={taxMode === '$' ? 'tax' : 'taxPercentage'}
                                                step=".01"
                                                min="0"
                                                onChange={HandleInputChange}
                                                onModeToggle={HandleTaxModeChange}
                                                mode={taxMode}
                                                value={taxMode === '$' ? quotUserEntry?.tax > 0 ? quotUserEntry?.tax : "" :
                                                    quotUserEntry?.taxPercentage > 0 ? quotUserEntry?.taxPercentage : ""}
                                                onKeyPress={(e) => { return PriceCheck(e.target.value) }}
                                                onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                                            />
                                        </div>

                                        <div className="mb-2">
                                            <label>Grand Total</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                placeholder="Enter"
                                                name="grandTotal"
                                                disabled
                                                value={values?.grandTotal > 0 ? FormatValueDecimal(displayAmount(values?.grandTotal)) : 0}
                                                style={{ textAlign: "right" }}
                                            ></input>
                                        </div>
                                    </div>
                                </div>
                                <div className="card d-none">
                                    <div className="card-header" id="headingTwo">
                                        <h2 className="mb-0">
                                            <button
                                                className="btn btn-link btn-block text-left"
                                                type="button"
                                                data-toggle="collapse"
                                                data-target="#collapseOne"
                                                aria-expanded="true"
                                                aria-controls="collapseOne"
                                            >
                                                Account Summary
                                            </button>
                                        </h2>
                                    </div>

                                    <div
                                        id="collapseOne"
                                        className="collapse show"
                                        aria-labelledby="headingOne"
                                        data-parent="#orderSummaryAccordion"
                                    >
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-12 mb-2">
                                                    <label>Credit Limit</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder=""
                                                    ></input>
                                                </div>
                                                <div className="col-md-12 mb-2">
                                                    <label>Outstanding</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder=""
                                                    ></input>
                                                </div>
                                                <div className="col-md-12 mb-2">
                                                    <label>Overdue</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder=""
                                                    ></input>
                                                </div>
                                                <div className="col-md-12 mb-2">
                                                    <label>Pending Orders</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder=""
                                                    ></input>
                                                </div>
                                                <div className="col-md-12 mb-2">
                                                    <label>Pending Delivery</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder=""
                                                    ></input>
                                                </div>
                                                <div className="col-md-12 mb-2">
                                                    <label>Available Room</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder=""
                                                    ></input>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <SpecialColorDetailsModal
                show={specialColorModalShow}
                onHide={() => setSpecialColorModalShow(false)}
                values={values}
                currencyDetail={currencyDetail}
                quotationId={factoryOrderRecord?.quotationId}
                filterToProducts={false}
                specialCharge={
                    quotUserEntry?.specialCharges ||
                    values?.specialCharges ||
                    values?.specialColorCharges ||
                    0
                }
            />
        </div>
    );
};

export default FactoryOrderProductSummary;
